import { createPortal } from 'react-dom';
import { Button } from '@/components/ui';
import type { PrintData, PrintDocumentData, Supplier } from '../api/taxInvoiceApi';
import { formatBusinessNo, won } from '../lib/taxInvoiceLib';
import { useTaxInvoiceLabels } from './TaxInvoiceSearchForm';
import '@/features/orders/printDocuments/printDocument.css';

/*
 * 세금계산서·계산서 인쇄 — BE print 응답(공급자 + 문서들)을 세금계산서 양식으로 출력한다.
 * 주문 출력물과 같은 인쇄 방식: 미리보기 모달, window.print() 시 @media print 가 .print-surface 만 남긴다.
 * 문서마다 쪽을 나눈다. 계산서(면세분)는 세액 칸이 없다.
 */

interface PartyProps {
  title: string;
  businessNo: string;
  companyName: string;
  ceoName: string;
  address: string;
  businessType: string;
  businessItem: string;
}

const Party = ({ title, businessNo, companyName, ceoName, address, businessType, businessItem }: PartyProps) => (
  <table>
    <tbody>
      <tr>
        <th rowSpan={4} style={{ width: 28, textAlign: 'center', writingMode: 'vertical-rl' }}>{title}</th>
        <th style={{ width: 80 }}>등록번호</th>
        <td colSpan={3} className="pd-num">{formatBusinessNo(businessNo) || '-'}</td>
      </tr>
      <tr>
        <th>상호</th>
        <td>{companyName || '-'}</td>
        <th style={{ width: 50 }}>성명</th>
        <td>{ceoName || '-'}</td>
      </tr>
      <tr>
        <th>사업장 주소</th>
        <td colSpan={3}>{address || '-'}</td>
      </tr>
      <tr>
        <th>업태</th>
        <td>{businessType || '-'}</td>
        <th>종목</th>
        <td>{businessItem || '-'}</td>
      </tr>
    </tbody>
  </table>
);

const InvoiceDocument = ({ doc, supplier, kindLabel }: { doc: PrintDocumentData; supplier: Supplier; kindLabel: string }) => {
  const isTax = doc.invoiceKind === 'TAX_INVOICE';
  return (
    <section className="tax-invoice-doc" style={{ breakAfter: 'page', pageBreakAfter: 'always', marginBottom: 32 }} data-testid="tax-invoice-print-doc">
      <h2 style={{ textAlign: 'center', fontSize: 20, fontWeight: 700, letterSpacing: 6, margin: '0 0 4px' }}>{kindLabel}</h2>
      <p style={{ textAlign: 'center', margin: '0 0 12px', fontSize: 12 }}>(공급받는자 보관용)</p>
      <p style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, margin: '0 0 6px' }}>
        <span>주문번호 {doc.orderNo}</span>
        <span>승인번호 {doc.approvalNo ?? '-'}</span>
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <Party title="공급자" {...supplier} />
        <Party
          title="공급받는자"
          businessNo={doc.buyer.businessNo}
          companyName={doc.buyer.companyName}
          ceoName={doc.buyer.ceoName}
          address={[doc.buyer.address, doc.buyer.addressDetail].filter(Boolean).join(' ')}
          businessType={doc.buyer.businessType}
          businessItem={doc.buyer.businessItem}
        />
      </div>
      <table style={{ marginTop: 8 }}>
        <thead>
          <tr>
            <th>작성일자</th>
            <th>품목</th>
            <th className="pd-num">공급가액</th>
            {isTax ? <th className="pd-num">세액</th> : null}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{doc.issueDate ?? '-'}</td>
            <td>{doc.itemSummary || '-'}</td>
            <td className="pd-num">{won(doc.supplyAmount)}</td>
            {isTax ? <td className="pd-num">{won(doc.vatAmount)}</td> : null}
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <th colSpan={isTax ? 3 : 2} style={{ textAlign: 'right' }}>합계금액</th>
            <td className="pd-num" style={{ fontWeight: 700 }}>{won(doc.totalAmount)}</td>
          </tr>
        </tfoot>
      </table>
      {doc.buyer.email ? <p style={{ fontSize: 12, marginTop: 6 }}>공급받는자 이메일 {doc.buyer.email}</p> : null}
    </section>
  );
};

export const TaxInvoicePrintModal = ({ data, onClose }: { data: PrintData; onClose: () => void }) => {
  const L = useTaxInvoiceLabels();
  const { t } = L;
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('orders.taxInvoice.print.title', '세금계산서 인쇄')}
      className="print-root fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4"
      style={{ background: 'var(--scrim-modal)' }}
      onClick={onClose}
    >
      <div className="my-8 w-full max-w-3xl rounded-xl shadow-[var(--elevation-3)]" style={{ background: 'var(--surface-0)' }} onClick={(e) => e.stopPropagation()}>
        <div className="print-hide flex items-center justify-between border-b px-6 py-4" style={{ borderColor: 'var(--divider-soft)' }}>
          <h2 className="text-lg font-semibold" style={{ color: 'var(--content-strong)' }}>
            {t('orders.taxInvoice.print.titleCount', { defaultValue: '세금계산서 인쇄 ({{count}}건)', count: data.documents.length })}
          </h2>
          <div className="flex gap-2">
            <Button size="sm" variant="primary" onClick={() => window.print()} data-testid="tax-invoice-print-go">
              {t('orders.taxInvoice.print.print', '인쇄')}
            </Button>
            <Button size="sm" variant="ghost" onClick={onClose}>{t('orders.taxInvoice.close', '닫기')}</Button>
          </div>
        </div>
        <div className="print-surface">
          {!data.supplier.businessNo ? (
            <p className="print-hide" role="alert" style={{ color: 'var(--status-warn-fg)', marginBottom: 12 }}>
              {t('orders.taxInvoice.print.noSupplier', '공급자 정보가 비어 있습니다. [세금계산서 설정]에서 공급자 정보를 저장해 주세요.')}
            </p>
          ) : null}
          {data.documents.map((doc) => (
            <InvoiceDocument key={doc.issueId} doc={doc} supplier={data.supplier} kindLabel={L.invoiceKind(doc.invoiceKind)} />
          ))}
        </div>
      </div>
    </div>,
    document.body,
  );
};
